import { apiService } from './api-service';

import { Account, AccountFlag, AccountNote, AccountPersonRole, AccountTag, AssessmentCadence, AssociationDuesLine, BankAccount, BankAccountKind, BankRegisterRow, BankStatementImport, BankTransfer, BankStatementImportRow, Bill, BillLineItem, BillStatus, BillWithLines, Budget, Charge, CollectionsAction, Credit, Deposit, DepositablePayment, DuplicateAccountGroup, FiscalPeriod, Fund, FundKind, GlCode, GlCodeAccountType, ImportBatch, ImportBatchDetails, ImportBatchKind, Invoice, InvoiceStatus, InvoiceWithCharges, Item, JournalEntry, JournalLine, JournalLineInput, JournalOnlyEntry, LateFeeRule, LedgerEntry, MemorizedBill, MemorizedBillCadence, MemorizedBillWithLines, MemorizedInvoice, MemorizedInvoiceCadence, MemorizedInvoiceWithLines, MonthClose, Payment, PaymentMethod, Person, PersonPosition, PersonPositionAssignment, Reconciliation, Refund, ReturnedPayment, Vendor, WorkOrderBillingPostResult, WorkOrderBillingPreview, WorkOrderBillingQueueItem, WorkOrderBillingState, WorkOrderQueueTab } from '@/types/models';
import type { BankStatementImportCreateInput, ImportRowUpdateInput } from '@/types/schemas/bank-import';
import type { BudgetCreateInput, BudgetUpdateInput } from '@/types/schemas/budget';
import type { CheckPrintRequestInput } from '@/types/schemas/check-print';
import type { CollectionsActionCreateInput, CollectionsActionUpdateInput } from '@/types/schemas/collections-action';
import type { DuesRounding } from '@/utils/accounting-dues';
import { getBusinessDateParts, todayBusinessMs } from '@/utils/date-utils';
import type { DuesScheduleRow } from '@/utils/dues-period';
import { triggerBlobDownload } from '@/utils/file-download';
import type { ResetStatus } from '@/utils/poll-association-reset';

/** POST /people body — `accountId` attaches the new person in the same write. */
export type PersonCreateBody = Partial<Person> & {
	accountId?: string;
	accountRole?: AccountPersonRole;
	/** On the link — an emergency contact's "Son". */
	accountRelationship?: string;
};

/** Journal line as enriched by `/bills/:id/journal-entries` — adds GL code/name
 *  via server-side LEFT JOIN so the bill detail page can render without a
 *  separate gl_codes lookup. */
export interface BillJournalLine extends JournalLine {
	glCode?: string;
	glName?: string;
}
export interface BillJournalEntry extends Omit<JournalEntry, 'lines'> {
	lines: BillJournalLine[];
}

export interface AssociationDuesResponse {
	associationId: string;
	associationName: string;
	amountCents?: number;
	cadence?: AssessmentCadence;
	dayOfMonth?: number;
	startOn?: number;
	/** When true, each account's dues = the annual operating budget ×
	 *  (account.parValueBps ÷ 10000) ÷ periods-per-year (#232). The toggle is
	 *  owned by AssociationSection in the dues settings UI. */
	parValue: boolean;
	/** Sum of `lines`. In par-value mode each line holds a GL code's ANNUAL budget,
	 *  so this total IS the annual operating budget. Server-derived — never stored. */
	linesTotalCents: number;
	/** @deprecated Hand-typed annual operating budget. Superseded by
	 *  `linesTotalCents`; still returned for associations configured before the
	 *  budget moved onto the GL lines. */
	operatingBudgetCents?: number;
	/** When true, par-value dues are rounded UP to the next whole dollar (#232). */
	/** @deprecated Legacy toggle (round up); read `rounding`. */
	roundToDollar?: boolean;
	/** Par-value rounding — each GL line rounded separately. */
	rounding?: DuesRounding;
	/** The GL template's line amounts in order — what per-line rounding rounds. */
	lineWeightsCents?: number[];
	/** Dues GL lines. Per-period amounts normally; ANNUAL budgets in par-value mode. */
	lines: AssociationDuesLine[];
}

interface PaginatedResponse<T> {
	data: T[];
	pagination?: { page: number; limit: number; total: number; pages: number };
}

// Report row shapes as returned by api/reports/accounting.ts — duplicated
// here so the frontend has real types without depending on the Worker code.
/** An account's mailing columns, carried on report rows so search and the Address cell can use them (#550). */
export interface ReportRowMailingFields {
	mailingAddress?: string;
	mailingCareOf?: string;
	mailingCity?: string;
	mailingState?: string;
	mailingZip?: string;
}

export interface DelinquencyAccount extends ReportRowMailingFields {
	accountId: string;
	accountNumber?: string;
	address?: string;
	balanceCents: number;
	oldestDueOn?: number;
	daysOverdue?: number;
}
export interface DelinquencyGroup {
	associationId: string;
	associationName: string;
	accounts: DelinquencyAccount[];
	totalCents: number;
}
export interface DelinquencyReport {
	generatedAt: number;
	params: { asOf?: number; minAgeDays?: number; associationId?: string };
	totals: { accountCount: number; totalCents: number };
	groups: DelinquencyGroup[];
}

export interface AssociationDashboard {
	generatedAt: number;
	association: {
		id: string; name: string;
		pmId?: string;
		duesAmountCents?: number;
		duesCadence?: 'monthly' | 'quarterly' | 'annual';
		duesDayOfMonth?: number;
		/** Par value on AND a positive budget — dues bill by par value (`isParValueBilling`). */
		parValueActive?: boolean;
		/** A positive flat amount, or par value over a budget (`isDuesConfigured`). Absent from older APIs. */
		duesConfigured?: boolean;
	};
	kpis: {
		accountCount: number;
		arCents: number;
		creditCents: number;
		zeroBalanceCount: number;
		owingCount: number;
		archivedCount: number;
	};
	aging: {
		current: { label: string; accountCount: number; totalCents: number };
		d31_60: { label: string; accountCount: number; totalCents: number };
		d61_90: { label: string; accountCount: number; totalCents: number };
		d90_plus: { label: string; accountCount: number; totalCents: number };
	};
	recent: Array<{
		kind: 'charge' | 'payment' | 'credit' | 'refund';
		id: string;
		accountId: string;
		accountNumber?: string;
		accountAddress?: string;
		occurredOn: number;
		signedAmountCents: number;
		category?: string;
		method?: string;
		memo?: string;
	}>;
}

export interface Statement {
	id: string;
	accountId: string;
	associationId: string;
	periodStart: number;
	periodEnd: number;
	r2Key: string;
	balanceStartCents: number;
	balanceEndCents: number;
	chargesCents: number;
	paymentsCents: number;
	creditsCents: number;
	refundsCents: number;
	itemCount: number;
	generatedBy?: string;
	generatedAt: string;
}

export interface OverviewAssociation {
	associationId: string;
	associationName: string;
	pmId?: string;
	pmName?: string;
	/** Assigned PM's chart color (`users.color`); used to tint the picker tile. */
	pmColor?: string;
	accountCount: number;
	arCents: number;
	creditCents: number;
	/** Accounts with a late-fee-eligible charge overdue 30+ days. */
	overdue30Count: number;
	scheduledTotalCents: number;
	primaryCadence?: 'monthly' | 'quarterly' | 'annual';
	/** Flat per-association dues from `associations.dues_amount_cents`. */
	duesAmountCents?: number;
	/** Cadence paired with `duesAmountCents`. */
	duesCadence?: 'monthly' | 'quarterly' | 'annual';
	/** `associations.dues_day_of_month` — with the cadence, when dues next post (#418). */
	duesDayOfMonth?: number;
	/** `associations.dues_start_on` (business date) — nothing posts before it. */
	duesStartOn?: number;
	/** Explicit Par Value toggle from `associations.par_value` — when true,
	 *  dues derive from each account's share of the operating budget (#232). */
	parValue: boolean;
	driftCount: number;
	driftLastRanAt: number | null;
}
export interface OverviewTotals {
	associationCount: number;
	accountCount: number;
	arCents: number;
	creditCents: number;
}
export interface OverviewByManager {
	pmId?: string;
	pmName: string;
	arCents: number;
	associationCount: number;
}
export interface OverviewReport {
	generatedAt: number;
	totals: OverviewTotals;
	associations: OverviewAssociation[];
	aging: {
		current: { label: string; accountCount: number; totalCents: number };
		d31_60: { label: string; accountCount: number; totalCents: number };
		d61_90: { label: string; accountCount: number; totalCents: number };
		d90_plus: { label: string; accountCount: number; totalCents: number };
	};
	byManager: OverviewByManager[];
}

export interface StatementEntry {
	kind: 'charge' | 'payment' | 'credit' | 'refund';
	id: string;
	occurredOn: number;
	signedAmountCents: number;
	amountCents: number;
	category?: string;
	method?: string;
	reference?: string;
	memo?: string;
	runningBalanceCents: number;
}
export interface AccountStatement {
	generatedAt: number;
	account: {
		id: string;
		accountNumber?: string;
		lotNumber?: string;
		address?: string;
		mailingAddress?: string;
		mailingCareOf?: string;
		mailingCity?: string;
		mailingState?: string;
		mailingZip?: string;
		externalName?: string;
		balanceCents: number;
	};
	association: {
		id: string;
		name: string;
		address?: string;
		city?: string;
		state?: string;
		zip?: string;
		statementLegalText?: string;
		statementCalloutText?: string;
		lateFeeAmountCents?: number;
		lateFeePercentBps?: number;
		lateFeeGraceDays?: number;
		/** Email of the PM (`associations.assigned_to`) — used on statement
		 *  contact strip so homeowners reply to the relationship owner. */
		pmEmail?: string;
	} | null;
	organization: {
		id: string;
		name?: string;
		address?: string;
		city?: string;
		state?: string;
		zip?: string;
		phone?: string;
		fax?: string;
		email?: string;
		website?: string;
		/** Multi-line remit-to address printed in the payment-instruction box.
		 *  Falls back to the composed office address when unset. */
		remitAddress?: string;
		/** Management-company logo (base64 data URL) printed in the header. */
		logo?: string;
		/** Brand color (hex) for envelope return-address text. */
		brandColor?: string;
	} | null;
	holders: Array<{
		firstName: string;
		lastName?: string;
		email?: string;
		phone?: string;
		companyName?: string;
		role: 'owner' | 'resident' | 'billing_contact' | 'emergency_contact';
	}>;
	window: { startDate: number; endDate: number; defaultStartDate: number };
	openingBalanceCents: number;
	closingBalanceCents: number;
	entries: StatementEntry[];
}

/**
 * Print payload for a single invoice. Mirrors the enrichment shape of
 * `AccountStatement` so the print page can reuse the envelope-window layout
 * (org block, To/Regarding boxes, remit-to, late-fee placeholders, contact
 * strip) — but with the running-balance ledger replaced by the invoice's
 * own line items.
 */
/** An account PUT: the account's fields, plus the request-only `parValueBpsExact` marker. */
export type AccountUpdateBody = Partial<Account> & { parValueBpsExact?: boolean };

export interface InvoicePrintBundle {
	generatedAt: number;
	invoice: InvoiceWithCharges;
	account: AccountStatement['account'] | null;
	/** Plus the invoice copy, which the slip prefers over the statement copy (#678). */
	association: (NonNullable<AccountStatement['association']> & {
		invoiceCalloutText?: string;
		invoiceLegalText?: string;
	}) | null;
	organization: AccountStatement['organization'];
	holders: AccountStatement['holders'];
}

export interface AgingBucket { label: string; accountCount: number; totalCents: number }
export interface AgingAccountRow extends ReportRowMailingFields {
	accountId: string;
	accountNumber: string | null;
	externalName: string | null;
	address: string | null;
	balanceCents: number;
	currentCents: number;
	d1_30Cents: number;
	d31_60Cents: number;
	d61_90Cents: number;
	d90PlusCents: number;
	totalCents: number;
	unallocatedCents: number;
}
export interface AgingTotals {
	currentCents: number;
	d1_30Cents: number;
	d31_60Cents: number;
	d61_90Cents: number;
	d90PlusCents: number;
	totalCents: number;
}
export interface AgingBeginningOfYear {
	asOf: number;
	fiscalYearStart: number;
	totalCents: number;
	accountCount: number;
}
export interface AgingReport {
	generatedAt: number;
	params: { asOf?: number; associationId?: string };
	beginningOfYear?: AgingBeginningOfYear;
	buckets: { current: AgingBucket; d31_60: AgingBucket; d61_90: AgingBucket; d90_plus: AgingBucket };
	/** Accounts carrying a credit (negative as-of balance). totalCents is negative. */
	credits?: { totalCents: number; accountCount: number };
	accounts?: AgingAccountRow[];
	totals?: AgingTotals;
}

export interface ApAgingVendorBucket { label: string; vendorCount: number; totalCents: number }
export interface ApAgingVendorRow {
	vendorId: string;
	vendorName: string | null;
	address: string | null;
	currentCents: number;
	d1_30Cents: number;
	d31_60Cents: number;
	d61_90Cents: number;
	d90PlusCents: number;
	totalCents: number;
	billCount: number;
}
export interface ApAgingTotals {
	currentCents: number;
	d1_30Cents: number;
	d31_60Cents: number;
	d61_90Cents: number;
	d90PlusCents: number;
	totalCents: number;
}
export interface ApAgingBeginningOfYear {
	asOf: number;
	fiscalYearStart: number;
	totalCents: number;
	vendorCount: number;
}
export interface ApAgingReport {
	generatedAt: number;
	params: { asOf?: number; associationId?: string; basis?: 'cash' | 'accrual' };
	beginningOfYear?: ApAgingBeginningOfYear;
	buckets: {
		current: ApAgingVendorBucket;
		d31_60: ApAgingVendorBucket;
		d61_90: ApAgingVendorBucket;
		d90_plus: ApAgingVendorBucket;
	};
	vendors: ApAgingVendorRow[];
	totals: ApAgingTotals;
}

export interface ProfitLossLine {
	glCodeId?: string;
	code?: string;
	label: string;
	accountType: string;
	/** Period (startDate–endDate) signed total in cents, natural-sided. */
	totalCents: number;
	/** Fiscal-year-to-date (fiscalYearStart–endDate) total in cents, same sign rule. */
	ytdCents: number;
}
export interface ProfitLossReport {
	generatedAt: number;
	params: {
		startDate: number;
		endDate: number;
		fiscalYearStart: number;
		associationId: string;
		basis: 'cash' | 'accrual';
	};
	totals: {
		incomeCents: number;
		expenseCents: number;
		netOrdinaryCents: number;
		otherIncomeCents: number;
		otherExpenseCents: number;
		/** Net of GLs that don't classify as income/expense but aren't Balance-Sheet (#184). */
		uncategorizedCents: number;
		netCents: number;
		ytdIncomeCents: number;
		ytdExpenseCents: number;
		ytdNetOrdinaryCents: number;
		ytdOtherIncomeCents: number;
		ytdOtherExpenseCents: number;
		ytdUncategorizedCents: number;
		ytdNetCents: number;
	};
	income: ProfitLossLine[];
	expenses: ProfitLossLine[];
	otherIncome: ProfitLossLine[];
	otherExpenses: ProfitLossLine[];
	/**
	 * GLs with activity that don't classify as income/expense and aren't a
	 * Balance-Sheet/non-posting account — oddly-typed (OTHER/null) flow accounts
	 * or unmapped gl_code_ids. Surfaced so the P&L always ties to the GL (#184).
	 */
	uncategorized: ProfitLossLine[];
	/** Parent-code → parent-name map for dotted-code hierarchy (e.g. "7000" → "ADMINISTRATIVE"). */
	parentLabels: Record<string, string>;
}

export interface BalanceSheetAccountRow {
	glCodeId: string;
	code: string;
	name: string;
	accountType: string;
	balanceCents: number;
	/** #460 — the linked bank account's own name (preferred over the GL name); absent when unlinked or ambiguous. */
	bankAccountName?: string;
}
export interface BalanceSheetGroup {
	rows: BalanceSheetAccountRow[];
	totalCents: number;
}
export interface BalanceSheetReport {
	generatedAt: number;
	params: { asOf: number; associationId: string; basis: 'cash' | 'accrual'; fiscalYearStart: number };
	assets: {
		cash: BalanceSheetGroup;
		accountsReceivable: BalanceSheetGroup;
		otherCurrent: BalanceSheetGroup;
		fixed: BalanceSheetGroup;
		currentTotalCents: number;
		totalCents: number;
	};
	liabilities: {
		accountsPayable: BalanceSheetGroup;
		otherCurrent: BalanceSheetGroup;
		longTerm: BalanceSheetGroup;
		currentTotalCents: number;
		totalCents: number;
	};
	equity: {
		rows: BalanceSheetAccountRow[];
		accountsTotalCents: number;
		/**
		 * Beginning Fund Balance — prior-period income/expense (not yet closed
		 * to an equity GL) plus opening cash/AR balances that have no journal
		 * counterpart. Acts as the equity plug so assets ≡ liabilities + equity.
		 */
		retainedEarningsCents: number;
		netIncomeCents: number;
		totalCents: number;
	};
	totalLiabilitiesAndEquityCents: number;
}

export interface GlReportLine {
	postedOn: number;
	debitCents: number;
	creditCents: number;
	// Canonical journal_entries.source: built-in posting kinds plus 'manual',
	// 'import', etc. Consumers should fall back to the raw string when the
	// label dictionary doesn't recognize a kind.
	sourceKind: string;
	sourceId: string;
	// Per-association journal_entries.entry_number (QB's Trans#) — the report's
	// hidden "Num" column. Null on cash basis, which is a projection with no
	// canonical entry to number.
	sourceRef: string | null;
	/**
	 * The SOURCE DOCUMENT number this posting came from — bill #, check #,
	 * invoice #, payment reference, deposit slip #, credit # — i.e. the "#"
	 * column (issue #486). Null when the source kind has no number to give
	 * (manual/opening/refund) or the row simply doesn't carry one.
	 *
	 * OPTIONAL on purpose: month-close freezes a whole GlReport payload
	 * (api/month-close.ts), so archives written before #486 have no such key.
	 */
	docNumber?: string | null;
	counterparty: string | null;
	memo: string | null;
}
export interface GlReportAccount {
	glCodeId: string | null;
	code: string;
	name: string;
	accountType: string;
	openingBalanceCents: number;
	lines: GlReportLine[];
	periodDebitCents: number;
	periodCreditCents: number;
	endingBalanceCents: number;
}
export interface GlReport {
	generatedAt: number;
	params: { associationId: string; startDate: number; endDate: number; basis: 'cash' | 'accrual' };
	accounts: GlReportAccount[];
	// Canonical accrual prior-period NI from journal_lines INC/EXP openings
	// (lines posted before startDate). Basis-INVARIANT — the cash/accrual chip
	// retimes in-period recognition only; this value never moves. Used by
	// Trial Balance to fold prior-year NI into Retained Earnings / Fund Balance.
	priorNetIncomeCents: number;
	trialBalance: {
		totalDebitsCents: number;
		totalCreditsCents: number;
		outOfBalanceCents: number;
		balanced: boolean;
	};
}

export interface Form1099Item {
	vendorId: string;
	name: string;
	printAs?: string;
	taxId?: string;
	hasTaxId: boolean;
	addressLine1?: string;
	addressLine2?: string;
	city?: string;
	state?: string;
	zip?: string;
	totalCents: number;
	paymentCount: number;
}
export interface Form1099Report {
	generatedAt: number;
	params: { year: number; thresholdCents: number; startDate: number; endDate: number };
	totals: { vendorCount: number; totalCents: number };
	items: Form1099Item[];
}

/**
 * Per-vendor row on the QB-style 1099 Summary report. Until the app has
 * 1099-box-to-GL mapping, `uncategorizedCents === totalCents` and the report
 * renders a single bucket — matching how QuickBooks shows unmapped activity.
 */
export interface Form1099SummaryItem {
	vendorId: string;
	name: string;
	printAs?: string;
	taxId?: string;
	hasTaxId: boolean;
	is1099: boolean;
	uncategorizedCents: number;
	totalCents: number;
	paymentCount: number;
}
export interface Form1099SummaryReport {
	generatedAt: number;
	params: { associationId: string; year: number; startDate: number; endDate: number };
	totals: { vendorCount: number; uncategorizedCents: number; totalCents: number };
	items: Form1099SummaryItem[];
}

export interface BudgetVsActualMonth {
	budgetCents: number;
	actualCents: number;
	varianceCents: number;
}
export interface BudgetVsActualLine {
	glCodeId: string;
	code?: string;
	name?: string;
	accountType?: GlCodeAccountType;
	months: BudgetVsActualMonth[];
	totalBudgetCents: number;
	totalActualCents: number;
	totalVarianceCents: number;
}
export interface BudgetVsActualReport {
	generatedAt: number;
	params: { associationId: string; fiscalYear: number; fundId?: string };
	budget: { id: string; name?: string; fundId?: string } | null;
	lines: BudgetVsActualLine[];
}

export interface PortfolioAssociationRow {
	associationId: string;
	associationName: string;
	pmId?: string;
	pmName?: string;
	accountCount: number;
	owingCount: number;
	delinquentCount: number;
	arCents: number;
	delinquencyRatio: number;
}
export interface PortfolioBankRow {
	bankAccountId: string;
	bankName: string;
	associationId: string;
	associationName?: string;
	balanceCents: number;
}
export interface PortfolioRecentRow {
	kind: 'charge' | 'payment';
	id: string;
	accountId: string;
	accountNumber?: string;
	accountAddress?: string;
	associationId: string;
	associationName?: string;
	occurredOn: number;
	signedAmountCents: number;
	memo?: string;
}
export interface PortfolioReport {
	generatedAt: number;
	totals: {
		associationCount: number;
		accountCount: number;
		owingCount: number;
		delinquentCount: number;
		arCents: number;
		cashCents: number;
		delinquencyRatio: number;
	};
	associations: PortfolioAssociationRow[];
	banks: PortfolioBankRow[];
	recent: PortfolioRecentRow[];
}

export interface CheckQueueRow {
	billId: string;
	billNumber?: string;
	vendorId: string;
	associationId: string;
	issuedOn: number;
	dueOn: number;
	totalCents: number;
	paidCents: number;
	balanceCents: number;
	memo?: string;
	payee: string;
	payeeAddressLine1?: string;
	payeeAddressLine2?: string;
	payeeCity?: string;
	payeeState?: string;
	payeeZip?: string;
}
export interface IssuedCheck {
	checkNumber: string;
	checkDateMs: number;
	payee: string;
	amountCents: number;
	memo?: string;
	bankAccount: string;
	payeeAddress1?: string;
	payeeAddress2?: string;
	payeeCity?: string;
	payeeState?: string;
	payeeZip?: string;
}
export interface CheckIssueResponse {
	bankAccountId: string;
	bankName: string;
	checkNumberStart: number;
	checkNumberEnd: number;
	checks: IssuedCheck[];
	multichaxTsv: string;
}

export interface CheckPrintLine {
	id: string;
	sortOrder: number;
	description: string | null;
	memo: string | null;
	amountCents: number;
	glCodeId: string | null;
	glCode: string | null;
	glName: string | null;
}
/** One bill on a check — a single stub row on the printed voucher. */
export interface CheckPrintBill {
	billId: string;
	billNumber: string | null;
	issuedOn: number;
	/**
	 * Cents THIS check applied to this bill — the amount the stub prints. Always
	 * sums across `bills[]` to the owning check's `amountCents`. Differs from
	 * `totalCents` whenever the check only partially pays the bill.
	 */
	appliedCents: number;
	/** The bill's FACE total. Context only — never what this check paid. */
	totalCents: number;
	/** Lifetime paid across ALL payments on the bill. Never this check's share. */
	paidCents: number;
	memo: string | null;
	lines: CheckPrintLine[];
}
/**
 * One physical check = one printed voucher. Groups every bill the check paid
 * into `bills[]`; `amountCents` is the CHECK's amount (sum of its linked
 * payments), not the sum of bill totals. Legacy payments with no `checks` row
 * are surfaced as a single-bill pseudo-check keyed by the payment id.
 */
export interface CheckPrintCheck {
	checkId: string;
	checkNumber: string | null;
	payee: string;
	payeeAddressLine1: string | null;
	payeeAddressLine2: string | null;
	payeeCity: string | null;
	payeeState: string | null;
	payeeZip: string | null;
	checkDate: number;
	amountCents: number;
	memo: string | null;
	bills: CheckPrintBill[];
	association: {
		id: string;
		name: string | null;
		address: string | null;
		city: string | null;
		state: string | null;
		zip: string | null;
	};
	/** The managing organization — printed as the check's payer/remit block
	 *  ("c/o {name}" + address + phone), not the association's own details (#320). */
	organization: {
		name: string | null;
		address: string | null;
		city: string | null;
		state: string | null;
		zip: string | null;
		phone: string | null;
	} | null;
	bank: {
		id: string;
		name: string | null;
		institution: string | null;
		accountLast4: string | null;
		routingLast4: string | null;
		accountFull: string | null;
		routingFull: string | null;
		addressLine1: string | null;
		addressLine2: string | null;
		city: string | null;
		state: string | null;
		zip: string | null;
		signatureImageKey: string | null;
	};
}
export interface CheckPrintData {
	checks: CheckPrintCheck[];
}

/**
 * A bill whose payment belongs to a physical check that pays MORE than one live
 * bill. `POST /bills/:id/void` refuses to half-reverse such a check (409
 * `MULTI_BILL_CHECK`); this shape carries the owning check so the caller can
 * offer a whole-check void via `voidCheck` instead.
 */
export interface MultiBillCheckConflict {
	checkId: string;
	checkNumber?: string;
	billCount?: number;
	message: string;
}
/** Result of `voidBill`: the voided bill, or the multi-bill-check conflict that
 *  blocks a single-bill void. */
export type VoidBillResult =
	| { ok: true; bill: Bill }
	| { ok: false; multiBillCheck: MultiBillCheckConflict };

export interface PaymentHistoryItem extends ReportRowMailingFields {
	paymentId: string;
	accountId: string;
	accountNumber?: string;
	associationId: string;
	associationName?: string;
	address?: string;
	receivedOn: number;
	amountCents: number;
	method: PaymentMethod;
	reference?: string;
	memo?: string;
}
export interface PaymentHistoryReport {
	generatedAt: number;
	params: { startDate?: number; endDate?: number; associationId?: string; accountId?: string };
	totals: { count: number; totalCents: number };
	items: PaymentHistoryItem[];
}

export interface JournalReportLine {
	id: string;
	sortOrder: number;
	debitCents: number;
	creditCents: number;
	memo: string | null;
	glCodeId: string | null;
	glCode: string | null;
	glName: string | null;
	accountId: string | null;
	accountLabel: string | null;
	bankAccountId: string | null;
	bankAccountName: string | null;
	fundId: string | null;
	fundName: string | null;
}
export interface JournalReportEntry {
	id: string;
	postedOn: number;
	memo: string | null;
	source: string | null;
	sourceRef: string | null;
	importBatchId: string | null;
	locked: boolean;
	reversesEntryId: string | null;
	lines: JournalReportLine[];
}
export interface JournalReportParams {
	associationId: string;
	startDate?: number;
	endDate?: number;
	sources?: string[];
	importBatchId?: string;
	glCodeIds?: string[];
	accountIds?: string[];
	bankAccountIds?: string[];
	entryIds?: string[];
	cursor?: string;
	limit?: number;
	sortBy?: 'postedOn' | 'sourceRef' | 'source' | 'memo' | 'lineCount' | 'total';
	sortDirection?: 'asc' | 'desc';
}
export interface JournalReport {
	generatedAt: number;
	params: {
		associationId: string;
		startDate: number;
		endDate: number;
		sources: string[];
		importBatchId: string | null;
		glCodeIds: string[];
		accountIds: string[];
		bankAccountIds: string[];
	};
	entries: JournalReportEntry[];
	nextCursor: string | null;
	totalCount: number;
}

const qs = (params: Record<string, unknown>): string => {
	const sp = new URLSearchParams();
	for (const [k, v] of Object.entries(params)) {
		if (v == null || v === '') continue;
		sp.set(k, String(v));
	}
	const s = sp.toString();
	return s ? `?${s}` : '';
};

export const buildReportPath = (slug: string, params: Record<string, unknown> = {}): string =>
	`/reports/accounting/${slug}${qs(params)}`;

const post = (path: string, body: unknown) =>
	apiService.fetchApi(path, {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify(body),
	});

const put = (path: string, body: unknown) =>
	apiService.fetchApi(path, {
		method: 'PUT',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify(body),
	});

const del = (path: string) =>
	apiService.fetchApi(path, { method: 'DELETE' });

/**
 * Thin wrappers over the accounting REST endpoints. Matches service layer
 * conventions used elsewhere (services/data-service.ts) — see there for the
 * broader patterns around error handling and pagination.
 */
export const accountingService = {
	// People — pass associationId to pull every person attached to any
	// account in the association in one request (each row gets an
	// `accountId` field via the server-side join).
	listPeople: (params: { accountId?: string; associationId?: string; q?: string; limit?: number } = {}) =>
		apiService.fetchApi(`/people${qs({ limit: 500, ...params })}`) as Promise<PaginatedResponse<Person & { accountId?: string; role?: AccountPersonRole }>>,
	/** Pass `accountId` (+ optional `accountRole`, default owner) to create AND attach in one atomic write. */
	createPerson: (body: PersonCreateBody) => post('/people', body) as Promise<Person>,
	updatePerson: (id: string, body: Partial<Person>) => put(`/people/${id}`, body) as Promise<Person>,
	attachPerson: (personId: string, accountId: string, role: AccountPersonRole, importBatchId?: string, relationship?: string) =>
		post(`/people/${personId}/accounts/${accountId}`, { personId, role, importBatchId, relationship }),
	detachPerson: (personId: string, accountId: string, role?: AccountPersonRole) =>
		del(`/people/${personId}/accounts/${accountId}${qs({ role })}`),

	// Person-positions lookup table (Trustee, President, …). GET is open
	// to anyone with VIEW_PEOPLE so the typeahead is usable; create / edit
	// require UPDATE_PEOPLE. See api/person-positions.ts for the gate.
	listPersonPositions: (params: { q?: string; limit?: number } = {}) =>
		apiService.fetchApi(`/person-positions${qs({ limit: 200, ...params })}`) as Promise<PaginatedResponse<PersonPosition>>,
	createPersonPosition: (body: { name: string; sortOrder?: number }) =>
		post('/person-positions', body) as Promise<PersonPosition>,

	// Person → Position assignment junction. Multiple per person (past
	// terms + concurrent positions). endDate omitted == currently active.
	listPersonPositionAssignments: (personId: string) =>
		apiService.fetchApi(`/people/${personId}/positions`) as Promise<{ data: PersonPositionAssignment[] }>,
	createPersonPositionAssignment: (personId: string, body: { positionId: string; startDate?: number; endDate?: number; notes?: string }) =>
		post(`/people/${personId}/positions`, body) as Promise<PersonPositionAssignment>,
	updatePersonPositionAssignment: (personId: string, assignmentId: string, body: { positionId?: string; startDate?: number; endDate?: number; notes?: string }) =>
		put(`/people/${personId}/positions/${assignmentId}`, body) as Promise<PersonPositionAssignment>,
	deletePersonPositionAssignment: (personId: string, assignmentId: string) =>
		del(`/people/${personId}/positions/${assignmentId}`),

	// Cross-association officer roll-up — used by the per-association
	// dashboard to render "President: Bob Dole" badges in one round-trip
	// instead of N per-person lookups.
	listAssociationOfficers: (associationId: string, activeOnly = true) =>
		apiService.fetchApi(
			`/person-positions/assignments${qs({ associationId, active: activeOnly ? '1' : '0' })}`,
		) as Promise<{ data: Array<PersonPositionAssignment & { personName: string }> }>,

	// Accounts
	listAccounts: (params: {
		associationId?: string; balanceGt?: number; status?: string;
		sortBy?: string; sortDir?: 'asc' | 'desc'; limit?: number;
		/** 1-based; `/accounts` reads it through `parsePagination`. Needed to exhaust the roster. */
		page?: number;
		includeArchived?: boolean; archivedOnly?: boolean;
		/** Server-side aging bucket filter (driven by dashboard cards). */
		ageBucket?: 'current' | 'd31_60' | 'd61_90' | 'd90_plus';
		/** Server-side balance bucket (driven by dashboard donut). */
		balanceFilter?: 'owing' | 'zero' | 'credit';
		asOf?: number;
	} = {}) =>
		apiService.fetchApi(`/accounts${qs({
			limit: 100, ...params,
			includeArchived: params.includeArchived ? 1 : undefined,
			archivedOnly: params.archivedOnly ? 1 : undefined,
		})}`) as Promise<PaginatedResponse<Account>>,
	getAccount: (id: string) => apiService.fetchApi(`/accounts/${id}`) as Promise<Account>,
	/** Accounts that look like duplicates of each other (portfolio-wide). */
	listDuplicateAccounts: () =>
		apiService.fetchApi('/accounts/duplicates') as Promise<{ data: DuplicateAccountGroup[] }>,
	// Fill a matched account's owner contact from a work order (issue #285). The
	// server only fills a blank — sets a missing owner phone, or creates the
	// owner when none exists and a name is given — so this is callable by roles
	// (e.g. Property Managers) that can't otherwise write people/accounts.
	saveOwnerContact: (accountId: string, body: { phone: string; name?: string }) =>
		post(`/accounts/${accountId}/owner-contact`, body) as Promise<{
			accountId: string; personId?: string; updated: boolean; created?: boolean; reason?: string;
		}>,
	// archivedAt is accepted as epoch-ms at create time (the server stores ms
	// and converts to ISO on read), so it's typed as `number | null | undefined`
	// here even though the read-shape Account.archivedAt is an ISO string.
	createAccount: (body: Omit<Partial<Account>, 'archivedAt'> & { archivedAt?: number | null }) =>
		post('/accounts', body) as Promise<Account>,
	/** `parValueBpsExact`: see AccountBase — marks parValueBps as exactly what the user set. */
	updateAccount: (id: string, body: AccountUpdateBody) => put(`/accounts/${id}`, body) as Promise<Account>,
	deleteAccount: (id: string) => del(`/accounts/${id}`),
	setAccountFlags: (id: string, flags: AccountFlag[]) =>
		put(`/accounts/${id}/flags`, { flags }) as Promise<{ ok: true; flags: AccountFlag[] }>,
	archiveAccount: (id: string) => post(`/accounts/${id}/archive`, {}) as Promise<Account>,
	unarchiveAccount: (id: string) => post(`/accounts/${id}/unarchive`, {}) as Promise<Account>,
	/** Transfer ownership: close+rename the old account, open a same-numbered
	 *  sibling for the new owner, and move the selected fully-unpaid charges. */
	transferAccount: (id: string, body: {
		transferDate: number;
		newOwner: { firstName: string; lastName?: string; email?: string; phone?: string; companyName?: string };
		moveChargeIds: string[];
	}) => post(`/accounts/${id}/transfer`, body) as Promise<{ newAccountId: string; account: Account }>,
	reapplyAccountPayments: (id: string) =>
		post(`/accounts/${id}/reapply-payments`, {}) as Promise<{
			accountId: string;
			paymentsTouched: number;
			chargesTouched: number;
		}>,
	getLedger: (id: string, asOf?: number) =>
		apiService.fetchApi(`/accounts/${id}/ledger${qs({ asOf })}`) as Promise<{ data: LedgerEntry[] }>,
	getStatement: (id: string, params: { startDate?: number; endDate?: number; asOf?: number; all?: 1 } = {}) =>
		apiService.fetchApi(`/accounts/${id}/statement${qs(params)}`) as Promise<AccountStatement>,

	// Money movements
	listCharges: (params: {
		associationId?: string;
		accountId?: string;
		outstandingOnly?: 0 | 1;
		effectiveOnly?: 0 | 1;
		limit?: number;
	} = {}) =>
		apiService.fetchApi(`/charges${qs({ limit: 500, ...params })}`) as Promise<
			PaginatedResponse<Charge & {
				associationId?: string;
				accountNumber?: string;
				accountAddress?: string;
				accountExternalName?: string;
				primaryOwnerName?: string;
			}>
		>,
	createCharge: (body: Partial<Charge>) => post('/charges', body) as Promise<Charge>,
	updateCharge: (id: string, body: Partial<Charge>) => put(`/charges/${id}`, body) as Promise<Charge>,
	deleteCharge: (id: string) => del(`/charges/${id}`),
	waiveCharge: (id: string, reason?: string) => post(`/charges/${id}/waive`, { reason }) as Promise<Charge>,
	unwaiveCharge: (id: string) => post(`/charges/${id}/unwaive`, {}) as Promise<Charge>,
	/** Display-only "written off" label — changes no application, balance or GL. */
	setChargeWrittenOff: (id: string, writtenOff: boolean) => (writtenOff
		? post(`/charges/${id}/written-off`, {})
		: del(`/charges/${id}/written-off`)) as Promise<Charge>,
	createPayment: (body: Partial<Payment>) => post('/payments', body) as Promise<Payment>,
	updatePayment: (id: string, body: Partial<Payment>) => put(`/payments/${id}`, body) as Promise<Payment>,
	deletePayment: (id: string) => del(`/payments/${id}`),
	voidPayment: (id: string, reason?: string) => post(`/payments/${id}/void`, { reason }) as Promise<Payment>,
	returnPayment: (body: {
		paymentId: string; returnedOn: number; reason?: string; memo?: string;
		nsfFeeCents?: number; nsfFeeGlCodeId?: string;
	}) => post('/returned-payments', body) as Promise<ReturnedPayment>,
	undoReturnedPayment: (id: string) => del(`/returned-payments/${id}`),
	getCredit: (id: string) => apiService.fetchApi(`/credits/${id}`) as Promise<Credit>,
	createCredit: (body: Partial<Credit>) => post('/credits', body) as Promise<Credit>,
	updateCredit: (id: string, body: Partial<Credit>) => put(`/credits/${id}`, body) as Promise<Credit>,
	deleteCredit: (id: string) => del(`/credits/${id}`),
	createRefund: (body: Partial<Refund>) => post('/refunds', body) as Promise<Refund>,
	updateRefund: (id: string, body: Partial<Refund>) => put(`/refunds/${id}`, body) as Promise<Refund>,
	deleteRefund: (id: string) => del(`/refunds/${id}`),

	// Deposits — cash/check payments are received individually, then grouped
	// onto a deposit slip via the Record Deposit workflow. The deposit row
	// is the canonical summary printed for the bank teller and used at
	// reconciliation (one ledger line per deposit, not per payment).
	listUndepositedPayments: (associationId: string) =>
		apiService.fetchApi(`/deposits/undeposited${qs({ associationId })}`) as Promise<{
			items: DepositablePayment[];
			totalCents: number;
		}>,
	listDeposits: (params: { associationId?: string; limit?: number } = {}) =>
		apiService.fetchApi(`/deposits${qs({ limit: 100, ...params })}`) as Promise<PaginatedResponse<Deposit>>,
	getDeposit: (id: string) =>
		apiService.fetchApi(`/deposits/${id}`) as Promise<Deposit>,
	getDepositPayments: (id: string) =>
		apiService.fetchApi(`/deposits/${id}/payments`) as Promise<{ items: DepositablePayment[] }>,
	createDeposit: (body: {
		associationId: string;
		bankAccountId: string;
		depositedOn: number;
		memo?: string;
		paymentIds: string[];
	}) => post('/deposits', body) as Promise<Deposit & { paymentIds: string[] }>,
	updateDeposit: (id: string, body: { bankAccountId?: string; depositedOn?: number; memo?: string | null }) =>
		put(`/deposits/${id}`, body) as Promise<Deposit>,
	markDepositPrinted: (id: string) =>
		post(`/deposits/${id}/print`, {}) as Promise<Deposit>,
	deleteDeposit: (id: string) => del(`/deposits/${id}`),

	// Statements
	listStatements: (accountId: string) =>
		apiService.fetchApi(`/statements${qs({ accountId })}`) as Promise<PaginatedResponse<Statement>>,
	generateStatement: (body: { accountId: string; periodStart: number; periodEnd: number }) =>
		post('/statements/generate', body) as Promise<Statement>,
	deleteStatement: (id: string) => del(`/statements/${id}`),
	/** Built once for UI download buttons — append the auth header handling
	 * via apiService's standard fetch if calling programmatically. */
	statementDownloadPath: (id: string) => `/statements/${id}/download`,

	// Notes
	listAccountNotes: (accountId: string) =>
		apiService.fetchApi(`/account-notes${qs({ accountId })}`) as Promise<PaginatedResponse<AccountNote>>,
	createAccountNote: (body: { accountId: string; body: string }) => post('/account-notes', body) as Promise<AccountNote>,
	updateAccountNote: (id: string, body: { body: string }) => put(`/account-notes/${id}`, body) as Promise<AccountNote>,
	deleteAccountNote: (id: string) => del(`/account-notes/${id}`),

	// Association dues
	// Account tags (editable flag registry, migration 170)
	getAccountTags: () =>
		apiService.fetchApi('/account-tags') as Promise<{ data: AccountTag[] }>,
	createAccountTag: (body: Partial<AccountTag>) =>
		post('/account-tags', body) as Promise<AccountTag>,
	updateAccountTag: (id: string, body: Partial<AccountTag>) =>
		put(`/account-tags/${id}`, body) as Promise<AccountTag>,
	deleteAccountTag: (id: string) =>
		del(`/account-tags/${id}`),

	getAssociationDues: (associationId: string) =>
		apiService.fetchApi(`/association-dues/${associationId}`) as Promise<AssociationDuesResponse>,
	/** Every association a portfolio-wide Post Dues run considers, with its cadence and dues day. */
	getDuesSchedule: () =>
		apiService.fetchApi('/accounting/post-dues') as Promise<{ associations: DuesScheduleRow[] }>,
	updateAssociationDues: (associationId: string, body: { amountCents?: number | null; cadence?: AssessmentCadence | null; dayOfMonth?: number | null; startOn?: number | null; parValue?: boolean; operatingBudgetCents?: number | null; roundToDollar?: boolean; rounding?: DuesRounding }) =>
		put(`/association-dues/${associationId}`, body),
	createDuesLine: (associationId: string, body: {
		glCodeId?: string | null;
		fundId?: string | null;
		/** Pins this line to a specific dues level. NULL/undefined = applies to
		 *  the association default (legacy pro-rate behavior). */
		levelId?: string | null;
		label?: string;
		amountCents: number;
		sortOrder?: number;
	}) => post(`/association-dues/${associationId}/lines`, body) as Promise<AssociationDuesLine>,
	updateDuesLine: (associationId: string, lineId: string, body: {
		glCodeId?: string | null;
		fundId?: string | null;
		levelId?: string | null;
		label?: string | null;
		amountCents?: number;
		sortOrder?: number;
	}) => put(`/association-dues/${associationId}/lines/${lineId}`, body),
	deleteDuesLine: (associationId: string, lineId: string) =>
		del(`/association-dues/${associationId}/lines/${lineId}`),

	// Late fee rules
	listLateFeeRules: (associationId: string) =>
		apiService.fetchApi(`/late-fee-rules${qs({ associationId })}`) as Promise<{ items: LateFeeRule[] }>,
	createLateFeeRule: (body: {
		associationId: string;
		graceDays: number;
		amountCents?: number;
		percentBps?: number;
		maxPerCycle?: number;
		capCents?: number;
		active?: boolean;
	}) => post('/late-fee-rules', body) as Promise<LateFeeRule>,
	updateLateFeeRule: (id: string, body: {
		graceDays?: number;
		amountCents?: number | null;
		percentBps?: number | null;
		maxPerCycle?: number;
		capCents?: number | null;
		active?: boolean;
	}) => put(`/late-fee-rules/${id}`, body) as Promise<LateFeeRule>,
	deleteLateFeeRule: (id: string) => del(`/late-fee-rules/${id}`),

	// Fiscal-period close
	listFiscalPeriods: (associationId: string) =>
		apiService.fetchApi(`/fiscal-periods${qs({ associationId })}`) as Promise<{ items: FiscalPeriod[] }>,
	closeFiscalPeriod: (body: { associationId: string; periodStart: number; periodEnd: number }) =>
		post('/fiscal-periods', body) as Promise<FiscalPeriod>,
	reopenFiscalPeriod: (id: string, body: { reason: string }) =>
		post(`/fiscal-periods/${id}/reopen`, body) as Promise<FiscalPeriod>,

	// Funds (segregate ledger activity per association: operating/reserve/etc).
	listFunds: (associationId: string) =>
		apiService.fetchApi(`/funds${qs({ associationId })}`) as Promise<{ items: Fund[] }>,
	createFund: (body: { associationId: string; name: string; kind: FundKind }) =>
		post('/funds', body) as Promise<Fund>,
	updateFund: (id: string, body: { name?: string; kind?: FundKind }) =>
		put(`/funds/${id}`, body) as Promise<Fund>,
	deleteFund: (id: string) => del(`/funds/${id}`),

	// Manual journal entries (balanced double-entry; foundation for inter-fund
	// transfers, accruals, depreciation).
	listJournalEntries: (
		associationId: string,
		params: { page?: number; limit?: number; sourceRefPrefix?: string } = {},
	) =>
		apiService.fetchApi(`/journal-entries${qs({ associationId, ...params })}`) as Promise<PaginatedResponse<JournalEntry>>,
	getJournalEntry: (id: string) =>
		apiService.fetchApi(`/journal-entries/${id}`) as Promise<JournalEntry>,
	createJournalEntry: (body: {
		associationId: string;
		postedOn: number;
		memo?: string;
		source?: string;
		sourceRef?: string;
		reversesEntryId?: string;
		lines: JournalLineInput[];
	}) => post('/journal-entries', body) as Promise<JournalEntry>,
	updateJournalEntry: (id: string, body: { postedOn?: number; memo?: string; lines?: JournalLineInput[] }) =>
		put(`/journal-entries/${id}`, body) as Promise<JournalEntry>,
	deleteJournalEntry: (id: string) => del(`/journal-entries/${id}`),

	// Bill-scoped journal entries (creation + every payment) — the API joins
	// `gl_codes` so each line carries the human-readable code/name without a
	// second client lookup.
	listBillJournalEntries: (billId: string) =>
		apiService.fetchApi(`/bills/${billId}/journal-entries`) as Promise<{
			data: BillJournalEntry[];
		}>,

	// Budgets
	listBudgets: (associationId: string, params: { fiscalYear?: number; page?: number; limit?: number } = {}) =>
		apiService.fetchApi(`/budgets${qs({ associationId, ...params })}`) as Promise<PaginatedResponse<Budget>>,
	getBudget: (id: string) =>
		apiService.fetchApi(`/budgets/${id}`) as Promise<Budget>,
	createBudget: (body: BudgetCreateInput) => post('/budgets', body) as Promise<Budget>,
	updateBudget: (id: string, body: BudgetUpdateInput) => put(`/budgets/${id}`, body) as Promise<Budget>,
	deleteBudget: (id: string) => del(`/budgets/${id}`),

	// Bank statement imports
	listBankImports: (bankAccountId: string) =>
		apiService.fetchApi(`/bank-imports${qs({ bankAccountId })}`) as Promise<{ data: BankStatementImport[] }>,
	getBankImport: (id: string) =>
		apiService.fetchApi(`/bank-imports/${id}`) as Promise<BankStatementImport>,
	createBankImport: (body: BankStatementImportCreateInput) =>
		post('/bank-imports', body) as Promise<BankStatementImport>,
	updateBankImportRow: (importId: string, rowId: string, body: ImportRowUpdateInput) =>
		apiService.fetchApi(`/bank-imports/${importId}/rows/${rowId}`, {
			method: 'PATCH', body: JSON.stringify(body),
		}) as Promise<BankStatementImportRow>,
	deleteBankImport: (id: string) => del(`/bank-imports/${id}`),

	// Collections workflow
	listCollectionsActions: (params: { accountId?: string; associationId?: string; actionType?: string; limit?: number } = {}) =>
		apiService.fetchApi(`/collections-actions${qs(params)}`) as Promise<{ data: CollectionsAction[] }>,
	createCollectionsAction: (body: CollectionsActionCreateInput) =>
		post('/collections-actions', body) as Promise<CollectionsAction>,
	updateCollectionsAction: (id: string, body: CollectionsActionUpdateInput) =>
		put(`/collections-actions/${id}`, body) as Promise<CollectionsAction>,
	deleteCollectionsAction: (id: string) => del(`/collections-actions/${id}`),

	// Check printing (AP)
	listCheckQueue: (associationId: string) =>
		apiService.fetchApi(`/checks/queue${qs({ associationId })}`) as Promise<{ data: CheckQueueRow[] }>,
	issueChecks: (body: CheckPrintRequestInput) =>
		post('/checks/issue', body) as Promise<CheckIssueResponse>,
	getCheckPrintData: (params: { paymentIds?: string[]; billIds?: string[] }) => {
		const sp = new URLSearchParams();
		if (params.paymentIds?.length) sp.set('paymentIds', params.paymentIds.join(','));
		if (params.billIds?.length) sp.set('billIds', params.billIds.join(','));
		return apiService.fetchApi(`/checks/print-data?${sp.toString()}`) as Promise<CheckPrintData>;
	},

	// Reports
	reportOverview: () =>
		apiService.fetchApi('/reports/accounting/overview') as Promise<OverviewReport>,
	reportAssociationDashboard: (associationId: string, asOf?: number) =>
		apiService.fetchApi(`/reports/accounting/association-dashboard${qs({ associationId, asOf })}`) as Promise<AssociationDashboard>,
	reportDelinquency: (params: { asOf?: number; minAgeDays?: number; associationId?: string } = {}) =>
		apiService.fetchApi(`/reports/accounting/delinquency${qs(params)}`) as Promise<DelinquencyReport>,
	reportPaymentHistory: (params: { startDate?: number; endDate?: number; associationId?: string; accountId?: string } = {}) =>
		apiService.fetchApi(`/reports/accounting/payment-history${qs(params)}`) as Promise<PaymentHistoryReport>,
	reportAging: (params: { asOf?: number; associationId?: string; detail?: 0 | 1; basis?: 'cash' | 'accrual' } = {}) =>
		apiService.fetchApi(`/reports/accounting/aging${qs(params)}`) as Promise<AgingReport>,
	reportApAging: (params: { asOf?: number; associationId?: string; basis?: 'cash' | 'accrual' } = {}) =>
		apiService.fetchApi(`/reports/accounting/ap-aging${qs(params)}`) as Promise<ApAgingReport>,
	reportProfitLoss: (params: { startDate: number; endDate: number; associationId: string; basis?: 'cash' | 'accrual' }) =>
		apiService.fetchApi(`/reports/accounting/profit-loss${qs(params)}`) as Promise<ProfitLossReport>,
	reportBalanceSheet: (params: { asOf?: number; associationId?: string; basis?: 'cash' | 'accrual' } = {}) =>
		apiService.fetchApi(`/reports/accounting/balance-sheet${qs(params)}`) as Promise<BalanceSheetReport>,
	reportGlReport: (params: {
		associationId: string;
		startDate: number;
		endDate: number;
		basis?: 'cash' | 'accrual';
	}) => apiService.fetchApi(`/reports/accounting/gl-report${qs(params)}`) as Promise<GlReport>,
	report1099: (params: { year?: number; thresholdCents?: number } = {}) =>
		apiService.fetchApi(`/reports/accounting/1099${qs(params)}`) as Promise<Form1099Report>,
	report1099Summary: (params: { associationId: string; year?: number }) =>
		apiService.fetchApi(`/reports/accounting/1099-summary${qs(params)}`) as Promise<Form1099SummaryReport>,
	reportBudgetVsActual: (params: { associationId: string; fiscalYear: number; fundId?: string }) =>
		apiService.fetchApi(`/reports/accounting/budget-vs-actual${qs(params)}`) as Promise<BudgetVsActualReport>,
	reportPortfolio: () =>
		apiService.fetchApi('/reports/accounting/portfolio') as Promise<PortfolioReport>,
	reportJournal: (params: JournalReportParams) => {
		const sp = new URLSearchParams();
		sp.set('associationId', params.associationId);
		if (params.startDate != null) sp.set('startDate', String(params.startDate));
		if (params.endDate != null) sp.set('endDate', String(params.endDate));
		if (params.sources?.length) sp.set('source', params.sources.join(','));
		if (params.importBatchId) sp.set('importBatchId', params.importBatchId);
		if (params.glCodeIds?.length) sp.set('glCodeIds', params.glCodeIds.join(','));
		if (params.accountIds?.length) sp.set('accountIds', params.accountIds.join(','));
		if (params.bankAccountIds?.length) sp.set('bankAccountIds', params.bankAccountIds.join(','));
		if (params.entryIds?.length) sp.set('entryIds', params.entryIds.join(','));
		if (params.cursor) sp.set('cursor', params.cursor);
		if (params.limit != null) sp.set('limit', String(params.limit));
		if (params.sortBy) sp.set('sortBy', params.sortBy);
		if (params.sortDirection) sp.set('sortDirection', params.sortDirection);
		return apiService.fetchApi(`/reports/accounting/journal?${sp.toString()}`) as Promise<JournalReport>;
	},
	reportJournalCsvUrl: (params: JournalReportParams): string => {
		const sp = new URLSearchParams();
		sp.set('associationId', params.associationId);
		if (params.startDate != null) sp.set('startDate', String(params.startDate));
		if (params.endDate != null) sp.set('endDate', String(params.endDate));
		if (params.sources?.length) sp.set('source', params.sources.join(','));
		if (params.importBatchId) sp.set('importBatchId', params.importBatchId);
		if (params.glCodeIds?.length) sp.set('glCodeIds', params.glCodeIds.join(','));
		if (params.accountIds?.length) sp.set('accountIds', params.accountIds.join(','));
		if (params.bankAccountIds?.length) sp.set('bankAccountIds', params.bankAccountIds.join(','));
		if (params.entryIds?.length) sp.set('entryIds', params.entryIds.join(','));
		if (params.sortBy) sp.set('sortBy', params.sortBy);
		if (params.sortDirection) sp.set('sortDirection', params.sortDirection);
		sp.set('format', 'csv');
		return `/reports/accounting/journal?${sp.toString()}`;
	},

	// GL codes (Chart of Accounts) — a single GLOBAL chart, shared across all associations.
	listGlCodes: (params: { accountType?: GlCodeAccountType; activeOnly?: boolean; q?: string; limit?: number; page?: number } = {}) =>
		apiService.fetchApi(`/gl-codes${qs({
			limit: 500, ...params,
			activeOnly: params.activeOnly ? 1 : undefined,
		})}`) as Promise<PaginatedResponse<GlCode>>,
	getGlCode: (id: string) => apiService.fetchApi(`/gl-codes/${id}`) as Promise<GlCode>,
	createGlCode: (body: Partial<GlCode>) => post('/gl-codes', body) as Promise<GlCode>,
	updateGlCode: (id: string, body: Partial<GlCode>) => put(`/gl-codes/${id}`, body) as Promise<GlCode>,
	deleteGlCode: (id: string) => del(`/gl-codes/${id}`),
	bulkUpsertGlCodes: (
		rows: Array<Partial<GlCode> & { code: string; name: string; accountType: GlCodeAccountType }>,
		options: { importBatchId?: string } = {},
	) =>
		post('/gl-codes/bulk', { rows, ...options }) as Promise<{ inserted: number; updated: number; skipped?: number; total: number }>,

	// Items — QB-style line-item presets that auto-populate invoice/charge
	// lines (item_code → description + gl_code_id + default_price_cents).
	// Pass `associationId` to get the "association overrides + global
	// defaults" resolved list; pass `defaultsOnly` to manage the templates.
	listItems: (params: { q?: string; activeOnly?: boolean; associationId?: string; defaultsOnly?: boolean; limit?: number } = {}) =>
		apiService.fetchApi(`/items${qs({
			limit: 200, ...params,
			activeOnly: params.activeOnly ? 1 : undefined,
			defaultsOnly: params.defaultsOnly ? 1 : undefined,
		})}`) as Promise<PaginatedResponse<Item>>,
	getItem: (id: string) => apiService.fetchApi(`/items/${id}`) as Promise<Item>,
	createItem: (body: Partial<Item>) => post('/items', body) as Promise<Item>,
	updateItem: (id: string, body: Partial<Item>) => put(`/items/${id}`, body) as Promise<Item>,
	deleteItem: (id: string) => del(`/items/${id}`),

	// Vendors — global payees, used for AP / 1099 reporting.
	listVendors: (params: { q?: string; activeOnly?: boolean; is1099?: boolean; vendorType?: string; limit?: number; page?: number } = {}) =>
		apiService.fetchApi(`/vendors${qs({ limit: 200, ...params, activeOnly: params.activeOnly ? 1 : undefined, is1099: params.is1099 ? 1 : undefined })}`) as Promise<PaginatedResponse<Vendor>>,
	getVendor: (id: string) => apiService.fetchApi(`/vendors/${id}`) as Promise<Vendor>,
	createVendor: (body: Partial<Vendor>) => post('/vendors', body) as Promise<Vendor>,
	updateVendor: (id: string, body: Partial<Vendor>) => put(`/vendors/${id}`, body) as Promise<Vendor>,
	deleteVendor: (id: string) => del(`/vendors/${id}`),
	purgeVendor: (id: string) =>
		post(`/vendors/${id}/purge`, {}) as Promise<{ purged: boolean; id: string }>,
	bulkUpsertVendors: (rows: Array<Partial<Vendor> & { name: string }>, importBatchId?: string) =>
		post('/vendors/bulk', { rows, importBatchId }) as Promise<{
			inserted: number; updated: number; total: number;
			errors: Array<{ name: string; externalId?: string; error: string }>;
		}>,

	// QuickBooks IIF customer-list bulk import: one POST per chunk creates N
	// (Account + Person + Owner-link) triples server-side via a single
	// env.DB.batch() call. The client chunks its parsed customer array and
	// loops, so we keep the bulk-import pattern (short POSTs) intact.
	bulkImportCustomers: (
		params: {
			associationId: string;
			rows: Array<{
				accountNumber?: string;
				/** The QB number before a closed row's "-N" suffix — finds a legacy closed account still on it. */
				baseAccountNumber?: string;
				address?: string;
				/** No unit address in the file — created with a NULL address. */
				addressMissing?: boolean;
				mailingAddress?: string;
				mailingCareOf?: string;
				mailingCity?: string;
				mailingState?: string;
				mailingZip?: string;
				/** QB-closed customer → `status = 'closed'`. */
				closed?: boolean;
				/** Business date from a full-date close marker. */
				closedOn?: number;
				lotNumber?: string;
				archivedAt?: number | null;
				externalName?: string;
				externalId?: string;
				flags?: import('@/types/models').AccountFlag[];
				firstName?: string;
				lastName?: string;
				email?: string;
				phone?: string;
				notes?: string;
				personExternalId?: string;
				/** Ownership share in bps of 100% (423 = 4.23%), par-value associations. */
				parValueBps?: number;
				attachOnly?: boolean;
			}>;
			importBatchId?: string;
		},
	) =>
		apiService.fetchApi('/customers/bulk', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(params),
			timeoutMs: 60_000,
		}) as Promise<{
			processed: number;
			accountsCreated: number;
			accountsUpdated: number;
			peopleCreated: number;
			attached: number;
			skipped: number;
			/** Archived accounts moved off a bare number a new active customer needed. */
			accountsRenumbered?: Array<{ from: string; to: string }>;
			/** Number-less accounts (journal-import auto-created) given the file's number. */
			accountsNumbered?: Array<{ externalName: string | null; number: string }>;
			/** A customer's account the file puts on a number another account holds — not renumbered. */
			accountNumberConflicts?: Array<{ externalName: string | null; number: string; heldBy: string | null; kept: string | null; message: string }>;
			errors: Array<{ rowIndex: number; accountNumber?: string; externalName?: string; message: string }>;
		}>,

	// Posts a utility bill register as one charge per resident. Idempotent on
	// (periodRef, accountId), so re-driving a chunk cannot double-charge.
	bulkPostWaterBills: (
		params: {
			associationId: string;
			importBatchId?: string;
			glCodeId: string;
			postedOn: number;
			dueOn: number;
			periodRef: string;
			periodLabel?: string;
			items: Array<{ accountId: string; amountCents: number; premise?: string }>;
		},
	) =>
		apiService.fetchApi('/water-bills/bulk', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(params),
			timeoutMs: 60_000,
		}) as Promise<{
			submitted: number;
			posted: number;
			skipped: number;
			errors: Array<{ accountId: string; message: string }>;
			glCode: string;
		}>,

	/**
	 * File the raw utility register in the association's Dropbox, under
	 * `Utilities/<year>/`, named for the month the billing period CLOSES.
	 *
	 * The name is built here rather than reusing the utility's own export name
	 * ("UT_BIL143_Standard (1).xlsx" tells a human nothing and collides with the
	 * next download). `YYYY-MM` leads so a year folder sorts chronologically.
	 *
	 * Falls back to the posting year when the register carried no parseable
	 * period — a register filed under the wrong year is still findable, whereas
	 * refusing to file it loses the document entirely.
	 */
	fileWaterBillRegister: async (
		params: { associationId: string; uri: string; fileName: string; periodEndMs?: number },
	): Promise<{ fileId: string; path: string[] }> => {
		// DYNAMIC on purpose. `utils/accounting-export` imports expo-document-picker
		// at the top level, which pulls in expo-modules-core — whose native globals
		// don't exist under vitest. A static import here would poison every spec
		// that touches accountingService, not just the ones exercising this method.
		const { readPickedFileBytes } = await import('@/utils/accounting-export');
		const bytes = await readPickedFileBytes(params.uri);

		const endMs = params.periodEndMs ?? todayBusinessMs();
		const { year, month } = getBusinessDateParts(endMs);
		const stamp = `${year}-${String(month).padStart(2, '0')}`;
		const ext = params.fileName.match(/\.[a-z0-9]+$/i)?.[0] ?? '.xlsx';

		const form = new FormData();
		form.append('file', new Blob([bytes]), params.fileName);
		form.append('fileName', `${stamp} Water Bill Register${ext}`);
		form.append('category', 'Utilities');
		form.append('year', String(year));

		return apiService.fetchApi(`/documents/association/${params.associationId}/upload`, {
			method: 'POST',
			body: form,
			timeoutMs: 60_000,
		}) as Promise<{ fileId: string; path: string[] }>;
	},

	// Sets ONLY par_value_bps on accounts that already exist. /customers/bulk
	// would also overwrite address/external_name on every customer row, so
	// correcting percentages through it would stomp hand-edited addresses.
	bulkSetParValue: (
		params: {
			associationId: string;
			importBatchId?: string;
			items: Array<{ accountId: string; parValueBps: number | null }>;
		},
	) =>
		apiService.fetchApi('/accounts/bulk-par-value', {
			method: 'POST',
			headers: { 'content-type': 'application/json' },
			body: JSON.stringify(params),
			timeoutMs: 60_000,
		}) as Promise<{
			submitted: number;
			updated: number;
			skipped: number;
			errors: Array<{ accountId: string; message: string }>;
		}>,

	// Import batches — the audit log that ties together every row created
	// during a bulk import. Super-admin can revert a batch in one shot.
	listImportBatches: (params: { associationId?: string; limit?: number } = {}) =>
		apiService.fetchApi(`/import-batches${qs({ limit: 100, ...params })}`) as Promise<PaginatedResponse<ImportBatch>>,
	getImportBatch: (id: string) => apiService.fetchApi(`/import-batches/${id}`) as Promise<ImportBatchDetails>,
	/**
	 * Download a raw uploaded bundle file (issue #248) — fetched through the
	 * authenticated API (so it picks up auth headers) and handed to the browser
	 * as a "Save as…". The r2Key must be one the batch's file list returned.
	 */
	downloadImportBatchFile: async (
		params: { importBatchId: string; associationId: string; r2Key: string; filename: string },
	): Promise<void> => {
		const path = `/accounting/qb-bundle/file?importBatchId=${encodeURIComponent(params.importBatchId)}`
			+ `&associationId=${encodeURIComponent(params.associationId)}&r2Key=${encodeURIComponent(params.r2Key)}`;
		const resp = await apiService.fetchApi(path, { method: 'GET' }, undefined, false, true);
		if (resp instanceof Response) {
			if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
			triggerBlobDownload(await resp.blob(), params.filename);
		}
	},
	/**
	 * The journal-only entries for a batch (issue #248) — posted to the ledger
	 * with no customer/vendor row. Used to review what a "complete" rebuild left
	 * unmatched.
	 */
	fetchJournalOnlyEntries: (params: { importBatchId: string; associationId: string }) =>
		apiService.fetchApi(
			`/accounting/qb-bundle/journal-only?importBatchId=${encodeURIComponent(params.importBatchId)}`
			+ `&associationId=${encodeURIComponent(params.associationId)}`,
		) as Promise<{ importBatchId: string; total: number; returned: number; truncated: boolean; rows: JournalOnlyEntry[] }>,
	/** Download just the journal-only entries as a CSV (issue #248). Web-only. */
	downloadJournalOnlyEntries: async (
		params: { importBatchId: string; associationId: string },
	): Promise<void> => {
		const path = `/accounting/qb-bundle/journal-only?importBatchId=${encodeURIComponent(params.importBatchId)}`
			+ `&associationId=${encodeURIComponent(params.associationId)}&format=csv`;
		const resp = await apiService.fetchApi(path, { method: 'GET' }, undefined, false, true);
		if (resp instanceof Response) {
			if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
			triggerBlobDownload(await resp.blob(), `journal-only-${params.importBatchId.slice(0, 8)}.csv`);
		}
	},
	/** Full per-association accounting reset (super-admin) — backs up then wipes.
	 *  Large associations run as a durable Workflow (mode:'workflow' + 202): poll
	 *  getAssociationResetStatus until done. Small/older deploys run synchronously
	 *  (mode:'sync') and return the deleted/backup counts directly. */
	resetAssociation: (body: { associationId: string; confirmName: string; skipBackup?: boolean }) =>
		post('/accounting/association-reset', body) as Promise<
			| { mode: 'workflow'; status: string; importBatchId: string; workflowId: string }
			| { mode: 'sync'; associationId: string; deletedTotal: number; deletedCounts: Record<string, number>; backupKey: string | null; downloadUrl: string | null }
		>,

	/** Poll a workflow-driven reset's progress. */
	getAssociationResetStatus: (importBatchId: string) =>
		apiService.fetchApi(`/accounting/association-reset/status?importBatchId=${encodeURIComponent(importBatchId)}`) as Promise<ResetStatus>,

	/** Download a pre-reset association backup snapshot by its R2 key. */
	downloadAssociationResetBackup: async (key: string): Promise<void> => {
		const path = `/accounting/association-reset/download?key=${encodeURIComponent(key)}`;
		const resp = await apiService.fetchApi(path, { method: 'GET' }, undefined, false, true);
		if (resp instanceof Response) {
			if (!resp.ok) throw new Error(`Download failed (${resp.status})`);
			triggerBlobDownload(await resp.blob(), key.split('/').pop() || 'association-backup.json');
		}
	},

	createImportBatch: (body: { kind: ImportBatchKind; associationId?: string; sourceFilename?: string; summary?: string }) =>
		post('/import-batches', body) as Promise<ImportBatch>,
	updateImportBatchSummary: (id: string, summary: string) =>
		put(`/import-batches/${id}`, { summary }) as Promise<ImportBatch>,
	revertImportBatch: (id: string) =>
		post(`/import-batches/${id}/revert`, {}) as Promise<{
			id: string;
			revertedAt: string;
			revertedBy: string;
			deletedCounts: Record<string, number>;
			recomputedAccountCount: number;
			/** Field values written back by an `account_repair` batch revert (#635). */
			fieldRestores?: { restored: number; skipped: number; unsupported: number };
		}>,

	/**
	 * Drive one chunk of post-import projection for a journal CSV batch.
	 * Set `reset=true` on the first call to clear `projected_at` and stale
	 * audit rows from previous failed attempts so the chunk loop can retry
	 * them. Subsequent calls in the same loop should leave `reset` off.
	 */
	/**
	 * One-off repair (issue: NSF not charged back): for each account with an
	 * imported returned payment, create the missing AR charge-back AND sweep the
	 * account's available credit onto it so it settles where the account is paid
	 * up. Cursor-paginated by account id — the caller loops passing
	 * `afterAccountId: nextAfterAccountId` until `done`. Super-admin only.
	 */
	backfillReturnedPaymentChargebacks: (opts?: { associationId?: string; chunkLimit?: number; afterAccountId?: string | null }) =>
		post(
			`/accounting/backfill-returned-payment-chargebacks${qs({
				chunkLimit: opts?.chunkLimit ?? 50,
				...(opts?.associationId ? { associationId: opts.associationId } : {}),
				...(opts?.afterAccountId ? { afterAccountId: opts.afterAccountId } : {}),
			})}`,
			{},
		) as Promise<{ created: number; swept: number; scanned: number; nextAfterAccountId: string | null; done: boolean; accountIds: string[] }>,

	/**
	 * Repair: re-run FIFO payment/credit allocation for EVERY customer in an
	 * association (fixes out-of-order import allocations leaving old charges
	 * unpaid). Launches the ReapplyPaymentsWorkflow (one account per step, breathes
	 * between) and returns the batch id to poll. Super-admin only.
	 */
	startReapplyAssociationPayments: (opts: { associationId: string }) =>
		post(`/accounting/reapply-association/start${qs({ associationId: opts.associationId })}`, {}) as Promise<{
			status: string; importBatchId: string; workflowId: string;
		}>,
	/** Poll the reapply workflow's progress (live step beacon + done flag). */
	getReapplyAssociationStatus: (opts: { importBatchId: string; associationId: string }) =>
		apiService.fetchApi(
			`/accounting/reapply-association/status${qs({ importBatchId: opts.importBatchId, associationId: opts.associationId })}`,
		) as Promise<{
			importBatchId: string;
			workflowId: string | null;
			workflowStatus: string | null;
			workflowError: string | null;
			currentStep: string | null;
			currentStepDetail: { output?: { account?: number; total?: number; chargesTouched?: number } } | null;
			currentStepAt: number | null;
			summary: { accounts?: number; paymentsTouched?: number; creditsTouched?: number; chargesTouched?: number } | null;
			done: boolean;
		}>,

	projectImportBatchChunk: (id: string, opts?: { reset?: boolean; chunkLimit?: number }) =>
		post(
			`/accounting/project-from-journal?importBatchId=${encodeURIComponent(id)}`
			+ `&chunkLimit=${opts?.chunkLimit ?? 25}${opts?.reset ? '&reset=1' : ''}`,
			{},
		) as Promise<{
			importBatchId: string;
			processed: number;
			projected: number;
			skipped: number;
			errors: Array<{ entryId: string; transactionId: string; message: string }>;
			remaining: number;
			done: boolean;
		}>,

	/**
	 * Bulk create charges/payments/credits in one round-trip. Items carry a
	 * discriminator `kind` so the server can dispatch each to the right
	 * table; balance recompute runs once per affected account at the end.
	 * Use this for CSV imports — hugely faster than per-row POSTs and robust
	 * against mid-import disconnects.
	 */
	// Bank accounts
	listBankAccounts: (params: { associationId?: string; activeOnly?: boolean; limit?: number; page?: number } = {}) =>
		apiService.fetchApi(`/bank-accounts${qs({
			limit: 200, ...params,
			activeOnly: params.activeOnly ? 1 : undefined,
		})}`) as Promise<PaginatedResponse<BankAccount>>,
	getBankAccount: (id: string) => apiService.fetchApi(`/bank-accounts/${id}`) as Promise<BankAccount>,
	// Opening balance is creation-only input (posted as an 'opening' journal
	// entry, never stored/edited on the row).
	createBankAccount: (body: Partial<BankAccount> & {
		associationId: string; name: string; kind: BankAccountKind;
		openingBalanceCents?: number;
		openingBalanceAsOf?: number | null;
	}) => post('/bank-accounts', body) as Promise<BankAccount>,
	updateBankAccount: (id: string, body: Partial<BankAccount>) =>
		put(`/bank-accounts/${id}`, body) as Promise<BankAccount>,
	deleteBankAccount: (id: string) => del(`/bank-accounts/${id}`),
	setDefaultBankAccount: (id: string) =>
		post(`/bank-accounts/${id}/set-default`, {}) as Promise<BankAccount>,
	getBankAccountRegister: (id: string, params: {
		from?: number;
		to?: number;
		limit?: number;
		page?: number;
		cursor?: string;
		kind?: string;
		all?: boolean;
		counts?: boolean;
	} = {}) =>
		apiService.fetchApi(`/bank-accounts/${id}/register${qs({
			...params,
			all: params.all ? 'true' : undefined,
			counts: params.counts ? 'true' : undefined,
		})}`, { timeoutMs: params.all ? 120_000 : 60_000, retry: false }) as Promise<{
			bankAccountId: string;
			currentBalanceCents: number;
			postedBalanceCents: number;
			unclearedCents: number;
			data: BankRegisterRow[];
			nextCursor: string | null;
			totalCount: number;
			pageOpeningBalanceCents?: number;
			kindCounts?: Record<string, number>;
		}>,
	getBankSubGlBalances: (id: string) =>
		apiService.fetchApi(`/bank-accounts/${id}/sub-gl-balances`) as Promise<{
			bankAccountId: string;
			glCodeId?: string;
			totalCents: number;
			lines: Array<{ glCodeId: string; glCode?: string; glName?: string; balanceCents: number; isBankGl: boolean }>;
		}>,
	recomputeBankAccountBalance: (id: string) =>
		post(`/bank-accounts/${id}/recompute-balance`, {}) as Promise<{ bankAccountId: string; currentBalanceCents: number }>,
	sweepUndepositedPayments: (id: string) =>
		post(`/bank-accounts/${id}/sweep-undeposited`, {}) as Promise<{
			bankAccountId: string; depositsCreated: number; paymentsLinked: number; totalCents: number;
		}>,

	// Bank transfers
	listBankTransfers: (params: { associationId?: string; bankAccountId?: string } = {}) =>
		apiService.fetchApi(`/bank-transfers${qs(params)}`) as Promise<{ data: BankTransfer[] }>,
	getBankTransfer: (id: string) =>
		apiService.fetchApi(`/bank-transfers/${id}`) as Promise<BankTransfer>,
	createBankTransfer: (body: {
		associationId: string;
		fromBankId: string;
		toBankId: string;
		transferredOn: number;
		amountCents: number;
		memo?: string;
		reference?: string;
	}) => post('/bank-transfers', body) as Promise<BankTransfer>,
	updateBankTransfer: (id: string, body: Partial<Pick<BankTransfer, 'memo' | 'reference' | 'fromClearedAt' | 'toClearedAt'>>) =>
		put(`/bank-transfers/${id}`, body) as Promise<BankTransfer>,
	deleteBankTransfer: (id: string) => del(`/bank-transfers/${id}`),

	// Reconciliations
	listReconciliations: (bankAccountId: string) =>
		apiService.fetchApi(`/reconciliations${qs({ bankAccountId })}`) as Promise<{ data: Reconciliation[] }>,
	getReconciliation: (id: string) =>
		apiService.fetchApi(`/reconciliations/${id}`) as Promise<Reconciliation & { clearedTotalCents: number }>,
	createReconciliation: (body: {
		bankAccountId: string; periodStart: number; periodEnd: number;
		beginningBalanceCents: number; endingBalanceCents: number; notes?: string;
	}) => post('/reconciliations', body) as Promise<Reconciliation>,
	updateReconciliation: (id: string, body: {
		periodStart?: number; periodEnd?: number;
		beginningBalanceCents?: number; endingBalanceCents?: number; notes?: string;
	}) => put(`/reconciliations/${id}`, body) as Promise<Reconciliation>,
	finishReconciliation: (
		id: string,
		body: { cleared: Array<{ kind: 'payment' | 'bill_payment' | 'transfer_out' | 'transfer_in' | 'journal' | 'returned_payment' | 'deposit'; transactionId: string }> },
	) => post(`/reconciliations/${id}/finish`, body) as Promise<Reconciliation>,
	undoReconciliation: (id: string) =>
		post(`/reconciliations/${id}/undo`, {}) as Promise<Reconciliation>,
	deleteReconciliation: (id: string) => del(`/reconciliations/${id}`),

	// Month-end close (finalize → serialize → lock → archive).
	listMonthCloses: (associationId: string) =>
		apiService.fetchApi(`/month-close${qs({ associationId })}`) as Promise<{ items: MonthClose[] }>,
	finalizeMonth: (body: { associationId: string; year: number; month: number }) =>
		post('/month-close', body) as Promise<MonthClose>,
	reopenMonthClose: (id: string, body: { reason: string }) =>
		post(`/month-close/${id}/reopen`, body) as Promise<MonthClose>,
	// Full archive bundle JSON — backs the "download backup" button.
	getMonthCloseBundle: (id: string) =>
		apiService.fetchApi(`/month-close/${id}/download`) as Promise<Record<string, unknown>>,
	// One frozen report payload pulled from the bundle (drives the archived
	// report print/view pages).
	getFrozenReport: (id: string, slug: string, basis?: 'cash' | 'accrual') =>
		apiService.fetchApi(`/month-close/${id}/report/${slug}${qs({ basis })}`) as Promise<{
			meta: Record<string, unknown>; basis: string; slug: string; data: unknown;
		}>,

	// Work-order billing — turning approved work orders into bills.
	/**
	 * NOTE the response shape: `{ items, total }`, NOT this file's
	 * `PaginatedResponse` (`{ data, pagination }`). Declared explicitly because
	 * `as Promise<T>` only ASSERTS a shape — typing it as PaginatedResponse
	 * compiled fine and silently produced an empty list in the UI.
	 */
	/**
	 * One tab of the work-order pipeline.
	 *
	 * `needs_review` and `pending_approval` are the upstream review stages (#496); the
	 * server answers them from a different FROM clause, but the response shape is the same
	 * queue-item envelope with the review fields populated.
	 *
	 * The cast asserts this shape rather than validating it — `{ items, total }` versus
	 * `{ data, pagination }` compiles clean either way and renders EMPTY when wrong, which
	 * no direct-call API spec can see. It matches the two `jsonResponse` calls in
	 * api/work-order-billing.ts's queue branch; change them together.
	 */
	workOrderBillingQueue: (params: {
		associationId?: string;
		status?: WorkOrderQueueTab;
		limit?: number;
		offset?: number;
	} = {}) =>
		apiService.fetchApi(`/work-order-billing/queue${qs({ limit: 200, ...params })}`) as Promise<{
			items: WorkOrderBillingQueueItem[];
			total: number;
			limit: number;
			offset: number;
			selfVendorConfigured: boolean;
			selfVendorName?: string;
			/**
			 * Whether `organizations.wo_billing_start_at` is set at all.
			 *
			 * FALSE means every tab is empty BY CONFIGURATION, not because the work is done —
			 * the cutoff fails closed, which is correct, but is indistinguishable from "nothing
			 * to bill" unless the page says so. Optional on the wire so a response from an
			 * older Worker deserialises rather than reading as `false` and showing a banner
			 * about a setting that is actually fine.
			 */
			billingConfigured?: boolean;
		}>,
	workOrderBillingPreview: (workOrderId: string) =>
		apiService.fetchApi(`/work-order-billing/${workOrderId}/preview`) as Promise<WorkOrderBillingPreview>,
	workOrderBillingState: (workOrderId: string) =>
		apiService.fetchApi(`/work-order-billing/${workOrderId}`) as Promise<WorkOrderBillingState>,
	workOrderBillingNextNumber: () =>
		apiService.fetchApi('/work-order-billing/next-number') as Promise<{
			nextInvoiceNumber: number; invoicePrefix: string; selfVendorConfigured: boolean;
		}>,
	postWorkOrderBill: (workOrderId: string, body: {
		invoiceNumber?: number | null;
		issuedOn?: number;
		dueOn?: number | null;
		/** A hand-chosen GL split. Must total the derived amount — the server enforces it. */
		lines?: Array<{ glCodeId: string; amountCents: number; description?: string }>;
	} = {}) =>
		post(`/work-order-billing/${workOrderId}/post`, body) as Promise<WorkOrderBillingPostResult>,
	/**
	 * One CHUNK of a multi-post. Never send more than the server's cap (25).
	 *
	 * `remaining` / `done` are scoped to the ids in THIS request, not to the whole
	 * queue — every submitted id comes back either in `results` or in `errors`, so
	 * `done` is reached even when an item fails permanently. `queueRemaining` is
	 * the portfolio-wide count and is informational only; looping until IT hits
	 * zero would never terminate with a permanently failing work order in the queue.
	 */
	commitWorkOrderBills: (body: { workOrderIds: string[]; issuedOn?: number; dueOn?: number | null }) =>
		post('/work-order-billing/commit', body) as Promise<{
			processed: number; posted: number;
			errors: Array<{ workOrderId: string; error: string }>;
			results: WorkOrderBillingPostResult[];
			remaining: number; queueRemaining: number; done: boolean;
		}>,
	skipWorkOrderBilling: (workOrderId: string, reason?: string) =>
		post(`/work-order-billing/${workOrderId}/skip`, { reason }) as Promise<{ workOrderId: string; status: string; skippedReason?: string }>,
	unskipWorkOrderBilling: (workOrderId: string) =>
		post(`/work-order-billing/${workOrderId}/unskip`, {}) as Promise<{ workOrderId: string; status: string }>,
	/** Invoiced outside the app: leaves the queue as `posted` with no bill row. */
	markWorkOrderBilled: (workOrderId: string, reference?: string) =>
		post(`/work-order-billing/${workOrderId}/mark-billed`, { reference }) as Promise<{
			workOrderId: string; status: string; billedExternally: true; externalReference?: string;
		}>,

	// Bills (AP)
	listBills: (params: { associationId?: string; vendorId?: string; status?: BillStatus; limit?: number } = {}) =>
		apiService.fetchApi(`/bills${qs({ limit: 200, ...params })}`) as Promise<PaginatedResponse<Bill>>,
	getBill: (id: string) => apiService.fetchApi(`/bills/${id}`) as Promise<BillWithLines>,
	createBill: (body: {
		associationId: string; vendorId: string;
		billNumber?: string; issuedOn: number; dueOn?: number | null;
		memo?: string; externalId?: string; externalRef?: string;
		lines: Array<Pick<BillLineItem, 'description' | 'glCodeId' | 'amountCents' | 'memo'> & { sortOrder?: number }>;
		importBatchId?: string;
	}) => post('/bills', body) as Promise<BillWithLines>,
	updateBill: (id: string, body: {
		billNumber?: string; issuedOn?: number; dueOn?: number | null;
		memo?: string; vendorId?: string;
		externalId?: string; externalRef?: string;
		lines?: Array<Pick<BillLineItem, 'description' | 'glCodeId' | 'amountCents' | 'memo'> & { sortOrder?: number }>;
	}) => put(`/bills/${id}`, body) as Promise<BillWithLines>,
	deleteBill: (id: string) => del(`/bills/${id}`),
	purgeBill: (id: string) => post(`/bills/${id}/purge`, {}) as Promise<{ purged: boolean; id: string; banksRebuilt: string[]; warning?: string }>,
	// Void goes through raw-response mode so a 409 MULTI_BILL_CHECK is surfaced as
	// a typed result (checkId + bill count) rather than a thrown string — the bill
	// detail page then offers a whole-check void. Every other non-2xx still throws
	// the standard `API error <status>` message classifyApiError understands.
	voidBill: async (id: string): Promise<VoidBillResult> => {
		const resp = await apiService.fetchApi(
			`/bills/${id}/void`,
			{ method: 'POST', headers: { 'content-type': 'application/json' }, body: JSON.stringify({}) },
			undefined, false, true,
		);
		if (!(resp instanceof Response)) return { ok: true, bill: resp as Bill };
		if (resp.status === 409) {
			const data = await resp.json() as { code?: string; checkId?: string; checkNumber?: string; message?: string };
			if (data.code === 'MULTI_BILL_CHECK' && data.checkId) {
				const m = (data.message ?? '').match(/pays (\d+) bills/);
				return {
					ok: false,
					multiBillCheck: {
						checkId: data.checkId,
						checkNumber: data.checkNumber,
						billCount: m ? Number(m[1]) : undefined,
						message: data.message ?? '',
					},
				};
			}
			throw new Error(`API error 409: ${data.message ?? 'Conflict'}`);
		}
		if (!resp.ok) throw new Error(`API error ${resp.status}: ${await resp.text()}`);
		return { ok: true, bill: await resp.json() as Bill };
	},
	/** Reverse EVERY bill a physical check paid, atomically (soft-deletes each
	 *  linked payment, reverses their journal entries, reopens the bills, and
	 *  recomputes the bank). */
	voidCheck: (id: string) =>
		post(`/checks/${id}/void`, {}) as Promise<{ voided: true; id: string; paymentsReversed: number }>,
	payBill: (id: string, body: {
		bankAccountId: string; paidOn: number; amountCents: number;
		method?: PaymentMethod; checkNumber?: string; reference?: string; memo?: string;
		importBatchId?: string;
	}) => post(`/bills/${id}/pay`, body) as Promise<{ id: string; billId: string; amountCents: number }>,
	bulkBills: (body: {
		importBatchId?: string;
		associationId: string;
		markCleared?: boolean;
		sections: Array<{
			vendorId: string;
			bills: Array<{
				tempId: string;
				billNumber?: string;
				issuedOn: number;
				dueOn?: number;
				memo?: string;
				externalRef?: string;
				expenseGlCodeId?: string;
				amountCents: number;
			}>;
			payments: Array<{
				billTempId: string;
				bankAccountId: string;
				paidOn: number;
				amountCents: number;
				method?: PaymentMethod;
				checkNumber?: string;
				memo?: string;
			}>;
		}>;
	}) => apiService.fetchApi('/bills/bulk', {
		method: 'POST',
		headers: { 'content-type': 'application/json' },
		body: JSON.stringify(body),
		// Bulk bill+payment+deposit insert across 100s of rows takes well past
		// the 30s default — bump to 2 min so the client doesn't abort while
		// the Worker is still committing rows it then can't tell us about.
		timeoutMs: 120_000,
	}) as Promise<{
		submittedSections: number;
		bills: number;
		payments: number;
		affectedBankAccountCount: number;
		depositsCreated: number;
		paymentsLinked: number;
		errors: Array<{ scope: 'bill' | 'payment'; sectionIndex: number; itemIndex: number; vendorId: string; message: string }>;
	}>,

	// Invoices (AR — wraps charges as line items)
	listInvoices: (params: { associationId?: string; accountId?: string; status?: InvoiceStatus; printQueued?: 0 | 1; limit?: number } = {}) =>
		apiService.fetchApi(`/invoices${qs({ limit: 200, ...params })}`) as Promise<PaginatedResponse<Invoice>>,
	getInvoice: (id: string) => apiService.fetchApi(`/invoices/${id}`) as Promise<InvoiceWithCharges>,
	getInvoicePrintBundle: (id: string) =>
		apiService.fetchApi(`/invoices/${id}/print-bundle`) as Promise<InvoicePrintBundle>,
	/** Many print bundles in one request (server caps at 100 ids); missing invoices are omitted. */
	getInvoicePrintBundles: (ids: string[]) =>
		apiService.fetchApi(`/invoices/print-bundles?ids=${ids.map(encodeURIComponent).join(',')}`) as Promise<{ data: InvoicePrintBundle[] }>,
	createInvoice: (body: {
		associationId: string; accountId: string;
		invoiceNumber?: string; issuedOn: number; dueOn?: number;
		memo?: string; externalId?: string; externalRef?: string;
		importBatchId?: string;
		lines: Array<{ chargeId?: string; amountCents?: number; postedOn?: number; dueOn?: number; category?: string; memo?: string }>;
	}) => post('/invoices', body) as Promise<InvoiceWithCharges>,
	updateInvoice: (id: string, body: {
		invoiceNumber?: string; issuedOn?: number; dueOn?: number | null; memo?: string;
		lines?: Array<{ chargeId?: string; amountCents?: number; postedOn?: number; dueOn?: number; category?: string; memo?: string }>;
	}) => put(`/invoices/${id}`, body) as Promise<InvoiceWithCharges>,
	deleteInvoice: (id: string) => del(`/invoices/${id}`),
	voidInvoice: (id: string) => post(`/invoices/${id}/void`, {}) as Promise<Invoice>,
	purgeInvoice: (id: string) =>
		post(`/invoices/${id}/purge`, {}) as Promise<{ purged: boolean; id: string; accountsRebuilt: string[] }>,
	setInvoicePrintQueue: (id: string, queued: boolean) =>
		post(`/invoices/${id}/print-queue`, { queued }) as Promise<Invoice>,
	bulkMarkInvoicesPrinted: (ids: string[]) =>
		post('/invoices/bulk-mark-printed', { ids }) as Promise<{ updated: number }>,

	// Memorized (recurring) invoices
	listMemorizedInvoices: (params: { associationId?: string; accountId?: string; active?: boolean; limit?: number } = {}) =>
		apiService.fetchApi(`/memorized-invoices${qs({ limit: 200, ...params })}`) as Promise<PaginatedResponse<MemorizedInvoice>>,
	getMemorizedInvoice: (id: string) => apiService.fetchApi(`/memorized-invoices/${id}`) as Promise<MemorizedInvoiceWithLines>,
	createMemorizedInvoice: (body: {
		associationId: string; accountId: string;
		name: string; cadence: MemorizedInvoiceCadence; dayOfMonth: number;
		startOn?: number | null; endOn?: number | null;
		active?: boolean; invoiceNumberPrefix?: string; memo?: string;
		lines: Array<{ amountCents: number; category?: string; memo?: string; sortOrder?: number }>;
	}) => post('/memorized-invoices', body) as Promise<MemorizedInvoiceWithLines>,
	updateMemorizedInvoice: (id: string, body: {
		name?: string; cadence?: MemorizedInvoiceCadence; dayOfMonth?: number;
		startOn?: number | null; endOn?: number | null;
		active?: boolean; invoiceNumberPrefix?: string | null; memo?: string | null;
		lines?: Array<{ amountCents: number; category?: string; memo?: string; sortOrder?: number }>;
	}) => put(`/memorized-invoices/${id}`, body) as Promise<MemorizedInvoiceWithLines>,
	deleteMemorizedInvoice: (id: string) => del(`/memorized-invoices/${id}`),

	// Memorized (recurring) bills
	listMemorizedBills: (params: { associationId?: string; vendorId?: string; active?: boolean; limit?: number } = {}) =>
		apiService.fetchApi(`/memorized-bills${qs({ limit: 200, ...params })}`) as Promise<PaginatedResponse<MemorizedBill>>,
	getMemorizedBill: (id: string) => apiService.fetchApi(`/memorized-bills/${id}`) as Promise<MemorizedBillWithLines>,
	createMemorizedBill: (body: {
		associationId: string; vendorId: string;
		name: string; cadence: MemorizedBillCadence; dayOfMonth: number;
		startOn?: number | null; endOn?: number | null;
		active?: boolean; billNumberPrefix?: string; memo?: string;
		lines: Array<{ amountCents: number; glCodeId?: string; description?: string; memo?: string; sortOrder?: number }>;
	}) => post('/memorized-bills', body) as Promise<MemorizedBillWithLines>,
	updateMemorizedBill: (id: string, body: {
		name?: string; vendorId?: string; cadence?: MemorizedBillCadence; dayOfMonth?: number;
		startOn?: number | null; endOn?: number | null;
		active?: boolean; billNumberPrefix?: string | null; memo?: string | null;
		lines?: Array<{ amountCents: number; glCodeId?: string; description?: string; memo?: string; sortOrder?: number }>;
	}) => put(`/memorized-bills/${id}`, body) as Promise<MemorizedBillWithLines>,
	deleteMemorizedBill: (id: string) => del(`/memorized-bills/${id}`),

	bulkTransactions: (body: {
		importBatchId?: string;
		markCleared?: boolean;
		items: Array<
			| { kind: 'charge'; accountId: string; postedOn: number; dueOn?: number; amountCents: number; category?: string; memo?: string }
			| { kind: 'payment'; accountId: string; receivedOn: number; amountCents: number; method?: PaymentMethod; reference?: string; memo?: string; bankAccountId?: string }
			| { kind: 'credit'; accountId: string; issuedOn: number; amountCents: number; memo?: string }
		>;
	}) => post('/transactions/bulk', body) as Promise<{
		submitted: number;
		charges: number;
		payments: number;
		credits: number;
		affectedAccountCount: number;
		affectedAccountIds: string[];
		depositsCreated: number;
		paymentsDeposited: number;
		errors: Array<{ index: number; message: string }>;
	}>,

	/**
	 * Seed day-one balances without importing QB history. `items` carries the
	 * net per-owner AR balance (positive = owes → opening charge; negative =
	 * prepaid → opening credit); `glItems` carries per-GL opening balances as
	 * the account's NORMAL balance (assets/expenses debit, liabilities/income
	 * credit), offset to opening equity. All rows are dated at `asOfMs`.
	 */
	bulkStartingBalances: (body: {
		associationId: string;
		importBatchId?: string;
		asOfMs: number;
		items?: Array<{ accountId: string; balanceCents: number }>;
		glItems?: Array<{ glCode: string; glName?: string; accountType?: GlCodeAccountType; balanceCents: number }>;
	}) => post('/accounting/import-starting-balances', body) as Promise<{
		submitted: number;
		posted: number;
		skipped: number;
		glPosted: number;
		glSkipped: number;
		affectedAccountCount: number;
		affectedAccountIds: string[];
		errors: Array<{ accountId?: string; glCode?: string; message: string }>;
	}>,
};

// Replace the earlier bulk upsert signatures so callers can pass batch IDs
// without threading extra options — the implementation above already accepts
// the second param.
